import { useState } from 'react';
import { Storage } from '../lib/storage';

// ─────────────────────────────────────────────────────────────────────
// useArchive — the finished-tournament archive, lifted out of the
// Padel24AppMain god component (see docs/history/DECOMPOSITION_PLAN.md, PR-D).
//
// Persistence is PER-ITEM, not a single blob: each archived tournament is one
// Storage entry keyed `tournament:<archiveId>`, and the in-memory `archive`
// array (`{ key, data }[]`) is rebuilt at mount by prefix-listing `tournament:`.
// There is NO `archive_v1` key and NO §13.20 blanket-persist effect — writes
// happen only through the explicit mutators below. `archiveLoaded` is a UI flag
// (HomeView's count/empty-state), NOT a persist-guard.
//
// As with useBookingSettings, the INITIAL load lives in the AppShell's atomic
// mount loader (it builds the `tournament:` list alongside ~13 other domains and
// flushes via setArchive/setArchiveLoaded). This hook therefore exposes those
// setters as a loader bridge. The cross-domain `archiveTournament(t)` writer
// (which also syncs allPlayers + tournament) lives in the AppShell and likewise
// uses the exposed setArchive — only the two self-contained mutators
// (deleteArchived, importArchive) live in the hook. The derivations that read
// `archive` (combinedArchive / archiveWithLive / archiveForArchive /
// effectivePlayerDB) also live in the AppShell, consuming this hook's `archive`.
// ─────────────────────────────────────────────────────────────────────

/** One archived tournament: its Storage key (`tournament:<id>`) + the payload. */
export interface ArchiveItem {
  key: string;
  data: any;
}

/**
 * One entry of the MEMBER's own history, as `/member/bootstrap` returns it.
 *
 * Deliberately not an `ArchiveItem`: this is the OUTPUT of
 * `getPlayerTournamentHistory`, not an input to it, and it carries only the
 * fields a member may see. Confusing the two is what blanked the member app.
 */
export interface MemberHistoryItem {
  key: string;
  tournament: any;
  playerInTournament: any;
  matches: any[];
  finalPosition: any;
}

export function useArchive() {
  const [archive, setArchive] = useState<ArchiveItem[]>([]); // finished tournaments
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  /** The member's own history, server-computed. Empty for a club user. */
  const [memberHistory, setMemberHistory] = useState<MemberHistoryItem[]>([]);
  /**
   * The signed-in member as the BOOTSTRAP describes them — `me`.
   *
   * Kept because its `id` is the one the player list is keyed by (the legacy
   * id), while the session carries the database uuid. Resolving «who am I»
   * against the wrong one of those two showed a member another member's name,
   * level and rating.
   */
  const [memberMe, setMemberMe] = useState<any>(null);

  // Delete an archived tournament. Storage-first: await delete, then drop from
  // state. (Copied verbatim from the former monolith.)
  const deleteArchived = async (key: string) => {
    try {
      await Storage.delete(key);
      setArchive((prev) => prev.filter((item) => item.key !== key));
    } catch (e) {
      console.error('Kunne ikke slette turnering:', e);
    }
  };

  // Import a backup (array of archived tournaments): write each as its own
  // `tournament:<id>` key, then merge into state (dedupe by key, re-sort by
  // archivedAt desc). Returns how many were imported. (Verbatim.)
  const importArchive = async (jsonData: any) => {
    if (!Array.isArray(jsonData)) {
      throw new Error('Ugyldig backup-format — forventet en liste av turneringer');
    }
    const imported: ArchiveItem[] = [];
    for (const t of jsonData) {
      if (!t || !t.archiveId) continue;
      const id = t.archiveId;
      const archivedData = { ...t, isArchived: true };
      const key = `tournament:${id}`;
      try {
        await Storage.set(key, JSON.stringify(archivedData));
        imported.push({ key, data: archivedData });
      } catch (e) {
        console.error('Kunne ikke importere turnering:', e);
      }
    }
    // Update state with both existing and imported (without duplicates)
    setArchive((prev) => {
      const merged = [...prev];
      imported.forEach((newItem) => {
        const idx = merged.findIndex((x) => x.key === newItem.key);
        if (idx >= 0) merged[idx] = newItem;
        else merged.push(newItem);
      });
      merged.sort((a, b) => (b.data.archivedAt || 0) - (a.data.archivedAt || 0));
      return merged;
    });
    return imported.length;
  };

  return {
    archive,
    archiveLoaded,
    // ── THE MEMBER'S OWN HISTORY, WHICH IS NOT AN ARCHIVE (#185) ────
    //
    // `/member/bootstrap` returns the member's history ALREADY COMPUTED —
    // `{ key, tournament, playerInTournament, matches, finalPosition }`, the
    // output of `getPlayerTournamentHistory`, field-filtered so one member's
    // payload carries no other member's details.
    //
    // It used to be handed to `setArchive`, and the two shapes are not the same
    // thing: an archive entry is `{ key, data }` where `data` is the whole
    // tournament. So every archive consumer read `entry.data` as undefined —
    // `archiveWithLive`'s sort threw inside a `useMemo` and the member app
    // rendered a WHITE SCREEN for anybody who had played a tournament. The dev
    // persona has no history, which is why it survived until a real member was
    // invited.
    //
    // And even without the crash it was wrong: the engine skips entries with no
    // `data`, so running it over these would have produced an EMPTY history from
    // a payload that already had the answer in it.
    memberHistory,
    setMemberHistory,
    memberMe,
    setMemberMe,
    // Exposed for the AppShell's atomic mount loader (initial flush) and for the
    // cross-domain archiveTournament(t) writer that lives in the AppShell.
    setArchive,
    setArchiveLoaded,
    deleteArchived,
    importArchive,
  };
}
